/**
 * Packs on the register.
 *
 * A pack is sold like a product — a tile, a ticket line, a quantity — so it is
 * given a product's shape here and everything from the grid to the receipt
 * handles it unchanged. Two things set it apart, both decided by the server:
 * its stock is how many the shelf can make up (`available`), and offers never
 * touch it (the pack price is the deal), which is why it carries no `offer`.
 */
import { isDoseItem } from "./doses";

export const PACK_CATEGORY = "pack";

const PACK_ID_PREFIX = "pack:";

export const isPackItem = (item) => item?.category === PACK_CATEGORY;

export const toPackItem = (pack) => {
  const slots = pack.items.filter((item) => item.variant_group_id);

  return {
    id: `${PACK_ID_PREFIX}${pack.id}`,
    pack_id: pack.id,
    category: PACK_CATEGORY,
    name: pack.name,
    image: pack.image,
    price: Number(pack.price),
    unit_price: Number(pack.price),
    discount_percentage: 0,
    offer: null,
    stock_quantity: pack.available,
    components: pack.items,
    // A range in the pack is a choice the cashier makes before it is added
    needs_choice: slots.length > 0,
    // Found by what is inside it too — "the kit with the Zeus" rings it up
    search_terms: [
      ...pack.items.map((item) => item.product_name),
      ...slots.flatMap((item) => item.options.map((option) => option.name)),
    ].join(" "),
  };
};

/** The picks of one slot as "variant ids", for the line's identity. */
const signature = (choices) =>
  choices
    .map((choice) => `${choice.item_id}=${choice.picks.map((p) => `${p.product_id}x${p.quantity}`).join("+")}`)
    .sort()
    .join(",");

/**
 * The pack with its slots filled, ready for the ticket: its own identity (two
 * packs with different choices are two lines, the same choices one line with a
 * quantity) and a name that says what is in it. `picks` is
 * `{ [item id]: { [product id]: quantity } }`.
 */
export const withChoices = (pack, picks) => {
  const choices = pack.components
    .filter((item) => item.variant_group_id)
    .map((item) => ({
      item_id: item.id,
      picks: Object.entries(picks[item.id] || {})
        .filter(([, quantity]) => quantity > 0)
        .map(([product_id, quantity]) => ({ product_id, quantity })),
    }));

  const labels = pack.components
    .filter((item) => item.variant_group_id)
    .flatMap((item) =>
      (picks[item.id] ? Object.entries(picks[item.id]) : [])
        .filter(([, quantity]) => quantity > 0)
        .map(([product_id, quantity]) => {
          const option = item.options.find((o) => o.product_id === product_id);
          return `${quantity > 1 ? `${quantity}× ` : ""}${option?.label || option?.name || ""}`;
        }),
    );

  return {
    ...pack,
    id: `${pack.id}:${signature(choices)}`,
    name: `${pack.name} · ${labels.join(", ")}`,
    choices,
    needs_choice: false,
  };
};

/**
 * How many picks a slot still needs, and whether every slot is complete.
 */
export const slotTotal = (picks, item) =>
  Object.values(picks[item.id] || {}).reduce((sum, quantity) => sum + quantity, 0);

export const isComplete = (pack, picks) =>
  pack.components
    .filter((item) => item.variant_group_id)
    .every((item) => slotTotal(picks, item) === item.quantity);

/**
 * Splits the ticket into the lists the sale endpoint takes: loose products
 * are priced one by one, packs are exploded server side, and doses are poured
 * from an open bottle (no stock moves for them).
 */
export const toSalePayload = (lines) => ({
  items: lines
    .filter((line) => !isPackItem(line.product) && !isDoseItem(line.product))
    .map((line) => ({ product_id: line.product.id, quantity: line.quantity })),
  doses: lines
    .filter((line) => isDoseItem(line.product))
    .map((line) => ({ product_id: line.product.dose_product_id, quantity: line.quantity })),
  packs: lines
    .filter((line) => isPackItem(line.product))
    .map((line) => ({
      pack_id: line.product.pack_id,
      quantity: line.quantity,
      ...(line.product.choices ? { choices: line.product.choices } : {}),
    })),
});
